import React, { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ButtonBase,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import moment from 'moment';

import {
  ISection,
  MEETING_SECTION_PAYMENT_TYPES,
} from '../../../../store/V2/SectionSlice';
import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../components/V2/Table/type1/TableStyles';
import {
  getClassShortUrlByWaitingId,
  getFormatedDurationByMinutes,
  getFormatedTableLabels,
  getShortName,
} from '../../../../helper';
import { IUser } from '../../../../store/UserSlice';
import { ICourse } from '../../../../store/V2/CoursesSlice';

interface UpcomingTableRowProps {
  section: ISection & { students: IUser[] };
  onStudentsClick: () => void;
}

const UpcomingTableRow: FunctionComponent<UpcomingTableRowProps> = ({
  section,
  onStudentsClick,
}) => {
  const navigate = useNavigate();

  // State
  const [tutorLinkToolTipText, setTutorLinkToolTipText] = useState('Copy Link');
  const [studentLinkToolTipText, setStudentLinkToolTipText] = useState(
    'Copy Link'
  );
  const [adminLinkToolTipText, setAdminLinkToolTipText] = useState('Copy Link');

  const tutorData = section.tutor as IUser;
  const courseData = section.course as ICourse;
  const startDateMoment = section.dateTime
    ? moment(section.dateTime)
    : undefined;

  const startDate = startDateMoment
    ? startDateMoment.format('ddd MMM DD YYYY')
    : '-';

  const startTime = startDateMoment ? startDateMoment.format('hh:mm A') : '-';
  const endTimeMoment =
    startDateMoment && section.duration
      ? startDateMoment.clone().add(section.duration, 'minutes')
      : undefined;

  const endDate = endTimeMoment ? endTimeMoment.format('ddd MMM DD YYYY') : '-';
  const endTime = endTimeMoment ? endTimeMoment.format('hh:mm A') : '-';
  const tutorJoinLink = section.tutorWaitingId
    ? getClassShortUrlByWaitingId(section.tutorWaitingId)
    : '';

  const studentJoinLink = section.studentWaitingId
    ? getClassShortUrlByWaitingId(section.studentWaitingId)
    : '';

  const adminJoinLink = section.adminWaitingId
    ? getClassShortUrlByWaitingId(section.adminWaitingId)
    : '';

  let tutorName = '';
  let courseTitle = '';
  let curriculum = '';
  let subject = '';
  let grade = '';
  let studentsButtonName = 'No students';

  if (tutorData) {
    tutorName = getShortName(tutorData.firstname, tutorData.lastname, true);
  }

  if (courseData) {
    courseTitle = courseData.title;
    curriculum = courseData.curriculum || '';
    subject = courseData.subject || '';
    grade = courseData.grade || '';
  }

  if (section.students && section.students.length) {
    studentsButtonName = getFormatedTableLabels({
      labels: section.students.map((studentItem) =>
        getShortName(studentItem.firstname, studentItem.lastname, true)
      ),
    });
  }

  const handleTutorLinkCopy = async () => {
    await window.navigator.clipboard.writeText(tutorJoinLink);

    setTutorLinkToolTipText('Link Copied');

    setTimeout(() => {
      setTutorLinkToolTipText('Copy Link');
    }, 1000);
  };

  const handleStudentLinkCopy = async () => {
    await window.navigator.clipboard.writeText(studentJoinLink);

    setStudentLinkToolTipText('Link Copied');

    setTimeout(() => {
      setStudentLinkToolTipText('Copy Link');
    }, 1000);
  };

  const handleAdminLinkCopy = async () => {
    await window.navigator.clipboard.writeText(adminJoinLink);

    setAdminLinkToolTipText('Link Copied');

    setTimeout(() => {
      setAdminLinkToolTipText('Copy Link');
    }, 1000);
  };

  const navigateToCourse = () => {
    if (courseData) {
      navigate(`${process.env.PUBLIC_URL}/portal/courses/${courseData._id}`);
    }
  };

  return (
    <StyledTableRow hover key={section._id}>
      <StyledTableCell>
        <Stack>
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            {startDate}
          </Typography>
          <Typography fontSize="12px" color="#94A3B8">
            {startTime}
          </Typography>
        </Stack>
      </StyledTableCell>
      <StyledTableCell>
        <Stack>
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            {endDate}
          </Typography>
          <Typography fontSize="12px" color="#94A3B8">
            {endTime}
          </Typography>
        </Stack>
      </StyledTableCell>
      <StyledTableCell>
        <Typography fontSize="14px" fontWeight={500} color="#1E293B">
          {section.duration
            ? getFormatedDurationByMinutes({ minutes: section.duration })
            : '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography fontSize="14px" fontWeight={500} color="#1E293B">
          {tutorName || '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <ButtonBase
          disabled={!(section.students || []).length}
          sx={{ textDecoration: 'underline', textAlign: 'unset' }}
          onClick={onStudentsClick}
        >
          {studentsButtonName}
        </ButtonBase>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          textTransform="capitalize"
        >
          {section.lessonType ? section.lessonType.toLowerCase() : '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        {section.paymentType ? (
          <Typography
            fontSize="14px"
            fontWeight={500}
            textTransform="capitalize"
            color={
              section.paymentType === MEETING_SECTION_PAYMENT_TYPES.BILLABLE
                ? 'success.main'
                : 'warning.main'
            }
          >
            {section.paymentType.toLowerCase().replace('_', ' ')}
          </Typography>
        ) : (
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            -
          </Typography>
        )}
      </StyledTableCell>
      <StyledTableCell>
        {courseTitle ? (
          <ButtonBase
            onClick={navigateToCourse}
            sx={{ textDecoration: 'underline', textAlign: 'unset' }}
          >
            {courseTitle}
          </ButtonBase>
        ) : (
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            -
          </Typography>
        )}
      </StyledTableCell>
      <StyledTableCell>
        <Typography fontSize="14px" fontWeight={500} color="#1E293B">
          {curriculum || '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography fontSize="14px" fontWeight={500} color="#1E293B">
          {subject || '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography fontSize="14px" fontWeight={500} color="#1E293B">
          {grade || '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        {tutorJoinLink ? (
          <Stack direction="row" alignItems="center" gap="5px">
            <Tooltip title={tutorLinkToolTipText}>
              <IconButton size="small" onClick={handleTutorLinkCopy}>
                <ContentCopyIcon sx={{ width: '15px', height: '15px' }} />
              </IconButton>
            </Tooltip>
            <ButtonBase
              sx={{ textDecoration: 'underline' }}
              href={tutorJoinLink}
              target="_blank"
            >
              Go to class
            </ButtonBase>
          </Stack>
        ) : (
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            -
          </Typography>
        )}
      </StyledTableCell>
      <StyledTableCell>
        {studentJoinLink ? (
          <Stack direction="row" alignItems="center" gap="5px">
            <Tooltip title={studentLinkToolTipText}>
              <IconButton size="small" onClick={handleStudentLinkCopy}>
                <ContentCopyIcon sx={{ width: '15px', height: '15px' }} />
              </IconButton>
            </Tooltip>
            <ButtonBase
              sx={{ textDecoration: 'underline' }}
              href={studentJoinLink}
              target="_blank"
            >
              Go to class
            </ButtonBase>
          </Stack>
        ) : (
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            -
          </Typography>
        )}
      </StyledTableCell>
      <StyledTableCell>
        {adminJoinLink ? (
          <Stack direction="row" alignItems="center" gap="5px">
            <Tooltip title={adminLinkToolTipText}>
              <IconButton size="small" onClick={handleAdminLinkCopy}>
                <ContentCopyIcon sx={{ width: '15px', height: '15px' }} />
              </IconButton>
            </Tooltip>
            <ButtonBase
              sx={{ textDecoration: 'underline' }}
              href={adminJoinLink}
              target="_blank"
            >
              Go to class
            </ButtonBase>
          </Stack>
        ) : (
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            -
          </Typography>
        )}
      </StyledTableCell>
    </StyledTableRow>
  );
};

export default UpcomingTableRow;
